import {
    CloseRounded,
    InfoOutlined,
    InfoRounded,
    MenuRounded,
    MinimizeRounded,
    MoreHorizRounded,
    SendRounded,
} from "@mui/icons-material";
import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    createTheme,
    IconButton,
    InputBase,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    MenuList,
    Paper,
    Popover,
    TextField,
    ThemeProvider,
    Toolbar,
    Tooltip,
    Typography,
    Link,
    Dialog,
    DialogContent,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AUTO_ANSWERS } from "../config";
import { finishChat } from "../requests/chats";
import { getMessages, sendMessage } from "../requests/messages";
import { removeSession } from "../store";
import messageNotificationAudio from "../media/pop.mp3";

const types = {
    image: "Ver imagen",
    video: "Abrir video",
    audio: "Abrir audio",
    file: "Descargar archivo",
    story_reply: "Respondio a esta historia",
    story_mention: "Te mencionó en esta historia",
};

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

const messageNotification = new Audio(messageNotificationAudio);

function MediaElem({ type, url }) {
    const [open, setOpen] = useState(false);
    const [gotErrorImg, setGotErrorImg] = useState(false);
    const [gotErrorVideo, setGotErrorVideo] = useState(false);

    let component;
    switch (type) {
        case "image":
            component = <img src={url} alt="Imagen" />;
            break;
        case "video":
            component = (
                <video controls autoPlay loop>
                    <source src={url + '&rand=' + new Date().getTime()} />
                    Contenido no disponible
                </video>
            );
            break;
        case "audio":
            return (
                <audio controls>
                    <source src={url + '&rand=' + new Date().getTime()} />
                    Contenido no disponible
                </audio>
            );
        case "file":
            return (
                <Link href={url + '&rand=' + new Date().getTime()} target="_blank">
                    {types[type]}
                </Link>
            );
        case "story_reply":
        case "story_mention":
            if (!gotErrorImg) {
                component = (
                    <img
                        src={url + '&rand=' + new Date().getTime()}
                        alt="Imagen"
                        onError={(e) => setGotErrorImg(true)}
                        style={{ maxHeight: "90vh" }}
                    />
                );
            } else if (!gotErrorVideo) {
                component = (
                    <video
                        controls
                        autoPlay
                        loop
                        onError={(e) => setGotErrorVideo(true)}
                        style={{ maxHeight: "90vh" }}
                    >
                        <source src={url + '&rand=' + new Date().getTime()} />
                        Contenido no disponible
                    </video>
                );
            } else {
                component = (
                    <Paper elevation={0} sx={{ p: 2 }}>
                        <Typography variant="body2">
                            Contenido no disponible
                        </Typography>
                    </Paper>
                );
            }
            break;
    }

    return (
        <>
            <Link onClick={(e) => setOpen(true)} sx={{ cursor: "pointer" }}>
                {types[type]}
            </Link>
            <Dialog open={open} onClose={(e) => setOpen(false)}>
                {open && (
                    <DialogContent sx={{ p: 0 }}>{component}</DialogContent>
                )}
            </Dialog>
        </>
    );
}

export default function Chat() {
    const _container = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { channel, chatId, name } = useParams();
    const chatInfo = useSelector((state) => state.chatInfo.value);
    const token = useSelector((state) => {
        return state.session.value ? state.session.value.token : null;
    });

    const [messages, setMessages] = useState([]);
    const previousMessages = usePrevious(messages);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [forceReload, setForceReload] = useState(false);
    const [menuEl, setMenuEl] = useState(null);

    // Respuestas rapidas
    const [answersMenu, setAnswersMenu] = useState(null);

    const handleChange = (handler) => (e) => handler(e.target.value);
    const handleSend = async (e) => {
        setLoading(true);
        const r = await sendMessage({ content: message, chatId, token });
        if (r.status !== "ok") {
            // enqueueSnackbar(r.message, {
            //     variant: "error",
            // });
        }
        setMessage("");
        setLoading(false);
        setForceReload(!forceReload);
    };
    const handleFinish = async (e) => {
        await finishChat({ chatId, token });
        navigate("/home/");
    };

    const handleAnswerMenu = (e) => {
        setAnswersMenu(e.currentTarget);
    };
    const handleCloseAnswerMenu = (e) => {
        setAnswersMenu(null);
    };

    const handleAnswerMenuOption = (val) => async (e) => {
        setLoading(true);
        const r = await sendMessage({ content: val, chatId, token });
        if (r.status !== "ok") {
            // enqueueSnackbar(r.message, {
            //     variant: "error",
            // });
        }
        setMessage("");
        setLoading(false);
        setForceReload(!forceReload);
    };

    useEffect(() => {
        if (!token) {
            dispatch(removeSession());
        }
    }, [token]);

    useEffect(() => {
        setMessage("");
        setLoading(true);

        if (token) {
            const load = async () => {
                const r = await getMessages({ token, chatId });
                setLoading(false);
                if (r.status === "error") {
                    if (r.message === "Token invalido") {
                        dispatch(removeSession());
                    } else {
                        // enqueueSnackbar(r.message, {
                        //     variant: "error",
                        // });
                    }
                } else {
                    setMessages(r.result);
                }
            };

            load();
            const interval = setInterval(() => {
                load();
            }, 1000);

            return () => {
                clearInterval(interval);
            };
        }
    }, [chatId, token, forceReload]);

    useEffect(() => {
        setMessages([]);
    }, [chatId]);

    useEffect(() => {
        if (
            _container &&
            previousMessages &&
            messages.length !== previousMessages.length
        ) {
            const { scrollHeight } = _container.current;
            _container.current.scrollTo({
                top: scrollHeight,
                behavior: "smooth",
            });

            if (
                messages.length > 0 &&
                messages[messages.length - 1].autor === "client"
            ) {
                // messageNotification.play();
            }
        }
    }, [messages]);

    return (
        <ThemeProvider
            theme={createTheme({
                palette: {
                    primary: {
                        main:
                            channel === "web"
                                ? "#4caf50"
                                : channel === "messenger"
                                ? "#2196f3"
                                : "#e91e63",
                        background:
                            channel === "web"
                                ? "#e8f5e9"
                                : channel === "messenger"
                                ? "#e3f2fd"
                                : "#fce4ec",
                    },
                    variant: "#eceff1",
                },
            })}
        >
            <div
                style={{
                    position: "relative",
                    flexGrow: 1,
                    width: "auto",
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column-reverse",
                    justifyContent: "end",
                    alignItems: "flex-start",
                }}
            >
                <Paper
                    component={"div"}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        width: "100%",
                        padding: "0.5rem",
                        bgcolor: "#eceff1",
                    }}
                    elevation={0}
                >
                    <Paper
                        component={"form"}
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            bgcolor: "white",
                            flexGrow: 1,
                            borderRadius: 8,
                        }}
                        elevation={0}
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (!loading) {
                                handleSend();
                            }
                        }}
                    >
                        <IconButton onClick={handleAnswerMenu}>
                            <MoreHorizRounded />
                        </IconButton>
                        <InputBase
                            placeholder="Escribe tu mensaje"
                            value={message}
                            onChange={handleChange(setMessage)}
                            sx={{ flex: 1, px: 1, pl: 2 }}
                        />
                        <IconButton
                            color="primary"
                            onClick={handleSend}
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress size={"24px"} />
                            ) : (
                                <SendRounded />
                            )}
                        </IconButton>
                    </Paper>
                </Paper>
                <div
                    style={{
                        display: "flex",
                        flexGrow: 1,
                        width: "100%",
                        flexDirection: "column",
                        overflow: "auto",
                        gap: "0.6rem",
                        padding: "1rem",
                    }}
                    ref={_container}
                >
                    <Toolbar />
                    {messages.map(
                        ({
                            _id,
                            content,
                            autor,
                            createdAt,
                            is_deleted,
                            attachments,
                        }) => (
                            <div
                                key={_id}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.2rem",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent:
                                            autor === "agent"
                                                ? "flex-end"
                                                : "flex-start",
                                    }}
                                >
                                    <Paper
                                        sx={{
                                            padding: 2,
                                            paddingBottom: 0.5,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0.5,
                                            bgcolor:
                                                autor === "agent"
                                                    ? "variant"
                                                    : "primary.background",
                                            maxWidth: "70%",
                                            minWidth: "25%",
                                            borderTopLeftRadius: 16,
                                            borderTopRightRadius: 16,
                                            borderBottomRightRadius:
                                                autor === "agent" ? 0 : 16,
                                            borderBottomLeftRadius:
                                                autor === "agent" ? 16 : 0,
                                            fontSize: "0.8rem",
                                            fontStyle: is_deleted
                                                ? "italic"
                                                : "normal",
                                        }}
                                        elevation={0}
                                    >
                                        {content}
                                        {attachments.length > 0 &&
                                            attachments.map(({ type, url }) => (
                                                <MediaElem
                                                    type={type}
                                                    url={url}
                                                />
                                            ))}
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                justifyContent:
                                                    autor === "agent"
                                                        ? "flex-end"
                                                        : "flex-start",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                fontSize={"0.6rem"}
                                            >
                                                {new Date(
                                                    createdAt
                                                ).toLocaleString()}
                                            </Typography>
                                        </div>
                                    </Paper>
                                </div>
                            </div>
                        )
                    )}
                </div>
                <AppBar
                    sx={{
                        bgcolor: "rgba(255, 255, 255, 0.7)",
                        backdropFilter: "blur(30px)",
                        boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    }}
                    elevation={0}
                    position="absolute"
                >
                    <Toolbar>
                        <Typography variant="title" sx={{ flexGrow: 1 }}>
                            {name}
                        </Typography>
                        {chatInfo && (
                            <Tooltip title="Ver información">
                                <IconButton
                                    size="small"
                                    onClick={(e) => setMenuEl(e.currentTarget)}
                                >
                                    <InfoOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Finalizar chat">
                            <IconButton size="small" onClick={handleFinish}>
                                <CloseRounded fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                </AppBar>
                {chatInfo && (
                    <Popover
                        open={Boolean(menuEl)}
                        anchorEl={menuEl}
                        onClose={(e) => setMenuEl(null)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        PaperProps={{
                            sx: {
                                px: 1,
                                width: "30vw",
                                maxHeight: "80vh",
                                overflowY: "auto",
                                boxShadow:
                                    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                            },
                            elevation: 0,
                        }}
                    >
                        <List>
                            {chatInfo &&
                                chatInfo.info &&
                                Object.keys(chatInfo.info).map((v) => {
                                    return (
                                        <ListItem disablePadding>
                                            <ListItemText
                                                secondary={chatInfo.info[v]}
                                                primary={
                                                    chatInfo.infoLabels
                                                        ? chatInfo.infoLabels[v]
                                                        : ""
                                                }
                                                secondaryTypographyProps={{
                                                    color: "textPrimary",
                                                    variant: "body1",
                                                }}
                                                primaryTypographyProps={{
                                                    color: "textSecondary",
                                                    variant: "caption",
                                                }}
                                            />
                                        </ListItem>
                                    );
                                })}
                        </List>
                    </Popover>
                )}
            </div>
            <Menu
                anchorEl={answersMenu}
                onClose={handleCloseAnswerMenu}
                open={Boolean(answersMenu)}
                elevation={0}
                PaperProps={{
                    style: {
                        boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    },
                }}
            >
                <MenuList dense>
                    {AUTO_ANSWERS.map(({ description, value }) => (
                        <MenuItem
                            onClick={handleAnswerMenuOption(value)}
                            key={description}
                        >
                            <ListItemText>{description}</ListItemText>
                        </MenuItem>
                    ))}
                </MenuList>
            </Menu>
        </ThemeProvider>
    );
}
